import React from "react";
import SeoHead from "../seo/SeoHead";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import TrustBadges from "../ui/TrustBadges";
import VehicleBrandGrid from "../ui/VehicleBrandGrid";
import WarrantyNotice from "../ui/WarrantyNotice";
import ServiceAreas from "../ui/ServiceAreas";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/AllFactory.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const AutoRepair = () => {
  return (
    <>
      <SeoHead
        title="Auto Repair in Temecula | Advanced Automotive Smog"
        description="Full-service auto repair in Temecula: diagnostics, brakes, engine work, and factory-scheduled maintenance. Call Advanced Automotive Smog for honest, skilled repairs."
        path="/auto-repair"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="Auto Repair"
          path="/auto-repair"
          description="Full-service auto repair in Temecula: diagnostics, brakes, engine work, and factory-scheduled maintenance."
          serviceType="Auto repair"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="Professional auto repair and maintenance"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>Auto repair · Temecula</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Auto Repair You Can Trust
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              From check-engine diagnostics to brakes and drivetrain work, our
              shop delivers thorough inspections and repairs so your vehicle
              stays safe, efficient, and reliable on California roads.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <TrustBadges tone="light" className="mt-12" />
        <VehicleBrandGrid tone="light" className="mt-10" linked />
        <WarrantyNotice tone="light" className="mt-8" />

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs You May Need This Service
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Warning lights or messages on the dash</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Strange noises: grinding, knocking, or squealing</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Performance issues: rough idle, poor acceleration, or overheating</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Fluid leaks, unusual smells, or vibration while driving</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Services Included
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                Computer diagnostics and drivability testing, including{" "}
                <Link to="/check-engine-light" className="text-teal-600 hover:underline">
                  check engine light diagnosis
                </Link>
              </span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Brake, steering, and suspension repairs</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Engine, cooling, and fuel system service</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                <Link to="/services/all-factory" className="text-teal-600 hover:underline">
                  Factory-scheduled maintenance
                </Link>{" "}
                and fluid services
              </span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Pre-purchase inspections and safety checks</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Choose Us
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            We are a local Temecula shop focused on clear communication and
            quality workmanship—not upsells you do not need. Our technicians
            take time to explain findings, prioritize safety, and help you make
            informed decisions about your vehicle.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <ServiceAreas tone="light" className="mt-12 border-t border-slate-200 pt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Book your repair or ask a question
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default AutoRepair;
